import { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { fmtDate } from "@/lib/locale";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  MessageSquare, ThumbsUp, ThumbsDown, Send, Trash2, ImagePlus, X, Plus,
  Bell, Search, ShieldAlert, MessageCircle, Loader2, Users,
  TrendingUp, User as UserIcon, Heart, Pencil, AtSign, Activity as ActivityIcon,
} from "lucide-react";
import type {
  SocialPostWithDetails, SocialCommentWithDetails,
  SocialNotificationWithDetails, SocialAuthor,
} from "@shared/schema";

interface AccessInfo {
  rescinded: boolean;
  isAdmin: boolean;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function timeAgo(dateStr: string | Date | null | undefined, t: TFunction): string {
  if (!dateStr) return "";
  const d = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return t("social:time.justNow");
  const mins = Math.floor(secs / 60);
  if (mins < 60) return t("social:time.minutes", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("social:time.hours", { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t("social:time.days", { count: days });
  return fmtDate(d);
}

// Render post/comment text with @mentions and #hashtags visually highlighted.
function RichText({ text }: { text: string }) {
  const parts = useMemo(() => text.split(/(\s+)/), [text]);
  return (
    <span className="whitespace-pre-wrap break-words">
      {parts.map((part, i) => {
        if (/^@[a-zA-Z0-9_]{1,40}$/.test(part)) {
          return <span key={i} className="text-violet-500 font-medium">{part}</span>;
        }
        if (/^#[a-zA-Z0-9_]{1,40}$/.test(part)) {
          return <span key={i} className="text-blue-500 font-medium">{part}</span>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

function objectUrl(path: string): string {
  if (path.startsWith("http") || path.startsWith("/")) return path;
  return `/${path}`;
}

// Avatar that renders the user's uploaded photo when present, falling back to initials.
function UserAvatar({
  author,
  className,
  fallbackClassName,
}: {
  author: { displayName: string; avatarUrl?: string | null };
  className?: string;
  fallbackClassName?: string;
}) {
  return (
    <Avatar className={`shrink-0 ${className ?? ""}`}>
      {author.avatarUrl && <AvatarImage src={objectUrl(author.avatarUrl)} alt={author.displayName} className="object-cover" />}
      <AvatarFallback className={fallbackClassName ?? "bg-violet-500/15 text-violet-500 font-semibold"}>
        {initialsOf(author.displayName)}
      </AvatarFallback>
    </Avatar>
  );
}

export default function DealerSocialPage() {
  const { t } = useTranslation(["social", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);

  const { data: access, isLoading: accessLoading } = useQuery<AccessInfo>({
    queryKey: ["/api/dealer-social/access"],
    queryFn: async () => (await apiRequest("GET", "/api/dealer-social/access")).json(),
    retry: false,
  });

  const { data: unread } = useQuery<{ count: number }>({
    queryKey: ["/api/dealer-social/notifications/unread-count"],
    queryFn: async () => (await apiRequest("GET", "/api/dealer-social/notifications/unread-count")).json(),
    refetchInterval: 30000,
    retry: false,
  });
  const activityCount = unread?.count ?? 0;

  if (accessLoading) {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (access?.rescinded) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <Card data-testid="card-rescinded">
          <CardContent className="py-12 flex flex-col items-center text-center gap-3">
            <ShieldAlert className="w-10 h-10 text-destructive" />
            <h2 className="text-lg font-semibold">{t("social:rescinded.title")}</h2>
            <p className="text-sm text-muted-foreground max-w-sm">
              {t("social:rescinded.description")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4" data-testid="page-dealer-social">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-violet-500" />
          <h1 className="text-xl font-bold tracking-tight">{t("social:title")}</h1>
        </div>
        <NotificationsBell />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="feed" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3" data-testid="tabs-social">
              <TabsTrigger value="feed" data-testid="tab-feed" className="gap-1.5">
                <MessageSquare className="w-4 h-4" /> {t("social:tabs.feed")}
              </TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity" className="gap-1.5">
                <ActivityIcon className="w-4 h-4" /> {t("social:tabs.activity")}
                {activityCount > 0 && (
                  <span
                    data-testid="badge-activity-unread"
                    className="ml-0.5 min-w-[16px] h-[16px] rounded-full bg-violet-500 text-white text-[9px] font-bold flex items-center justify-center px-1"
                  >
                    {activityCount > 99 ? "99+" : activityCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="profile" data-testid="tab-profile" className="gap-1.5">
                <UserIcon className="w-4 h-4" /> {t("social:tabs.profile")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="feed" className="space-y-4 mt-0">
              <div className="flex justify-end">
                <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="button-open-composer" className="gap-1.5">
                      <Plus className="w-4 h-4" /> {t("social:newPost")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="dialog-composer">
                    <DialogHeader>
                      <DialogTitle>{t("social:newPost")}</DialogTitle>
                    </DialogHeader>
                    <Composer onPosted={() => setComposerOpen(false)} />
                  </DialogContent>
                </Dialog>
              </div>

              <form
                onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }}
                className="flex items-center gap-2"
              >
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t("social:search.placeholder")}
                    className="pl-8"
                  />
                </div>
                {search && (
                  <Button type="button" variant="ghost" size="sm" data-testid="button-clear-search"
                    onClick={() => { setSearch(""); setSearchInput(""); }}>
                    {t("social:search.clear")}
                  </Button>
                )}
                <Button type="submit" variant="secondary" size="sm" data-testid="button-search">{t("social:search.submit")}</Button>
              </form>

              <Feed search={search} isAdmin={!!access?.isAdmin} />
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              <ActivityFeed />
            </TabsContent>

            <TabsContent value="profile" className="mt-0">
              <ProfileView isAdmin={!!access?.isAdmin} />
            </TabsContent>
          </Tabs>
        </div>

        <aside className="hidden lg:block">
          <TrendingTags
            onSelect={(tag) => { setSearch(tag); setSearchInput(tag); }}
          />
        </aside>
      </div>
    </div>
  );
}

function TrendingTags({ onSelect }: { onSelect: (tag: string) => void }) {
  const { t } = useTranslation(["social", "common"]);
  const { data } = useQuery<{ tags: { tag: string; count: number }[] }>({
    queryKey: ["/api/dealer-social/trending"],
    queryFn: async () => (await apiRequest("GET", "/api/dealer-social/trending")).json(),
  });
  const tags = data?.tags ?? [];

  return (
    <Card className="sticky top-4" data-testid="card-trending">
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-semibold">{t("social:trending.title")}</span>
        </div>
        {tags.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t("social:trending.empty")}</p>
        ) : (
          <div className="space-y-1">
            {tags.map((tag) => (
              <button
                key={tag.tag}
                type="button"
                data-testid={`trending-${tag.tag}`}
                onClick={() => onSelect(`#${tag.tag}`)}
                className="w-full flex items-center justify-between rounded-md px-2 py-1.5 text-left hover:bg-muted transition-colors"
              >
                <span className="text-sm font-medium text-blue-500 truncate">#{tag.tag}</span>
                <span className="text-xs text-muted-foreground shrink-0">{t("social:trending.posts", { count: tag.count })}</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ProfileData {
  author: SocialAuthor;
  posts: SocialPostWithDetails[];
  stats: { postCount: number; likesReceived: number; commentsReceived: number };
  editable: { displayName: string | null; avatarUrl: string | null };
}

function ProfileView({ isAdmin }: { isAdmin: boolean }) {
  const { t } = useTranslation(["social", "common"]);
  const { data, isLoading } = useQuery<ProfileData>({
    queryKey: ["/api/dealer-social/profile"],
    queryFn: async () => (await apiRequest("GET", "/api/dealer-social/profile")).json(),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card data-testid="profile-empty">
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          {t("social:profile.loadError")}
        </CardContent>
      </Card>
    );
  }

  const { author, posts, stats, editable } = data;

  return (
    <div className="space-y-4" data-testid="profile-view">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <UserAvatar
              author={author}
              className="w-16 h-16"
              fallbackClassName="bg-violet-500/15 text-violet-500 text-lg font-bold"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold truncate" data-testid="text-profile-name">{author.displayName}</h2>
                  <p className="text-sm text-muted-foreground" data-testid="text-profile-handle">@{author.username}</p>
                </div>
                <EditProfileDialog editable={editable} />
              </div>
              <div className="flex items-center gap-5 mt-3">
                <div className="flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-semibold" data-testid="stat-posts">{stats.postCount}</span>
                  <span className="text-xs text-muted-foreground">{t("social:profile.posts")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Heart className="w-4 h-4 text-rose-500" />
                  <span className="text-sm font-semibold" data-testid="stat-likes">{stats.likesReceived}</span>
                  <span className="text-xs text-muted-foreground">{t("social:profile.likes")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MessageCircle className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-semibold" data-testid="stat-comments">{stats.commentsReceived}</span>
                  <span className="text-xs text-muted-foreground">{t("social:profile.replies")}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {posts.length === 0 ? (
        <Card data-testid="profile-no-posts">
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {t("social:profile.noPosts")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => <PostCard key={post.id} post={post} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}

function EditProfileDialog({ editable }: { editable: { displayName: string | null; avatarUrl: string | null } }) {
  const { t } = useTranslation(["social", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState(editable.displayName ?? "");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(editable.avatarUrl);
  const fileRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading } = useUpload({
    onError: (err) => toast({ title: t("social:toasts.uploadFailed"), description: err.message, variant: "destructive" }),
  });

  function reset() {
    setDisplayName(editable.displayName ?? "");
    setAvatarUrl(editable.avatarUrl);
  }

  const save = useMutation({
    mutationFn: async () => {
      const trimmed = displayName.trim();
      return (await apiRequest("PATCH", "/api/dealer-social/profile", {
        displayName: trimmed.length > 0 ? trimmed : null,
        avatarUrl,
      })).json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/profile"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/feed"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/notifications"] });
      toast({ title: t("social:toasts.profileUpdated") });
      setOpen(false);
    },
    onError: (err: any) => toast({ title: t("social:toasts.couldNotSave"), description: err.message, variant: "destructive" }),
  });

  async function handleFile(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast({ title: t("social:toasts.onlyImages"), variant: "destructive" });
      return;
    }
    const res = await uploadFile(file);
    if (res?.objectPath) setAvatarUrl(res.objectPath);
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 shrink-0" data-testid="button-edit-profile">
          <Pencil className="w-3.5 h-3.5" /> {t("social:editProfile.button")}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="dialog-edit-profile">
        <DialogHeader>
          <DialogTitle>{t("social:editProfile.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-4">
            <UserAvatar
              author={{ displayName: displayName.trim() || "?", avatarUrl }}
              className="w-16 h-16"
              fallbackClassName="bg-violet-500/15 text-violet-500 text-lg font-bold"
            />
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleFile(e.target.files)}
                data-testid="input-avatar-file"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="gap-1.5"
                disabled={isUploading}
                onClick={() => fileRef.current?.click()}
                data-testid="button-upload-avatar"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                {avatarUrl ? t("social:editProfile.changePhoto") : t("social:editProfile.addPhoto")}
              </Button>
              {avatarUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={() => setAvatarUrl(null)}
                  data-testid="button-remove-avatar"
                >
                  {t("social:editProfile.remove")}
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="display-name">{t("social:editProfile.displayName")}</Label>
            <Input
              id="display-name"
              data-testid="input-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t("social:editProfile.displayNamePlaceholder")}
              maxLength={60}
            />
            <p className="text-xs text-muted-foreground">{t("social:editProfile.displayNameHelp")}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="button-cancel-profile">{t("social:editProfile.cancel")}</Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || isUploading}
            data-testid="button-save-profile"
            className="gap-1.5"
          >
            {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("social:editProfile.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Human-readable description of a notification, distinguishing likes from dislikes.
function notificationLabel(n: SocialNotificationWithDetails, t: TFunction): string {
  switch (n.type) {
    case "mention": return t("social:notifications.mention");
    case "comment": return t("social:notifications.comment");
    case "reaction": {
      const target = n.commentId ? t("social:notifications.targetComment") : t("social:notifications.targetPost");
      if (n.reactionType === "like") return t("social:notifications.likedTarget", { target });
      if (n.reactionType === "dislike") return t("social:notifications.dislikedTarget", { target });
      return t("social:notifications.reactedTarget", { target });
    }
    default: return t("social:notifications.interacted");
  }
}

function activityIcon(n: SocialNotificationWithDetails) {
  if (n.type === "mention") return <AtSign className="w-3.5 h-3.5 text-violet-500" />;
  if (n.type === "comment") return <MessageCircle className="w-3.5 h-3.5 text-blue-500" />;
  if (n.type === "reaction" && n.reactionType === "dislike") return <ThumbsDown className="w-3.5 h-3.5 text-destructive" />;
  if (n.type === "reaction") return <ThumbsUp className="w-3.5 h-3.5 text-violet-500" />;
  return <Bell className="w-3.5 h-3.5 text-muted-foreground" />;
}

function ActivityFeed() {
  const { t } = useTranslation(["social", "common"]);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<{ items: SocialNotificationWithDetails[] }>({
    queryKey: ["/api/dealer-social/notifications"],
    queryFn: async () => (await apiRequest("GET", "/api/dealer-social/notifications")).json(),
  });

  const markRead = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/dealer-social/notifications/read", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/notifications/unread-count"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/notifications"] });
    },
  });

  const items = data?.items ?? [];
  const hasUnread = items.some((n) => !n.read);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card data-testid="activity-empty">
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          {t("social:activity.empty")}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="activity-feed">
      {hasUnread && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            data-testid="button-mark-all-read"
            disabled={markRead.isPending}
            onClick={() => markRead.mutate()}
          >
            {t("social:activity.markAllRead")}
          </Button>
        </div>
      )}
      <Card>
        <CardContent className="p-0">
          {items.map((n) => (
            <div
              key={n.id}
              data-testid={`activity-${n.id}`}
              className={`flex items-start gap-3 px-4 py-3 border-b last:border-b-0 ${n.read ? "" : "bg-violet-500/5"}`}
            >
              <div className="relative shrink-0">
                <UserAvatar author={n.actor} className="w-9 h-9" fallbackClassName="bg-violet-500/15 text-violet-500 text-xs font-semibold" />
                <span className="absolute -bottom-1 -right-1 bg-background rounded-full p-0.5 border">
                  {activityIcon(n)}
                </span>
              </div>
              <div className="flex-1 min-w-0 text-sm">
                <span className="font-semibold" data-testid={`activity-actor-${n.id}`}>{n.actor.displayName}</span>{" "}
                <span className="text-muted-foreground">{notificationLabel(n, t)}</span>
                {n.postPreview && <p className="text-xs text-muted-foreground truncate mt-0.5">"{n.postPreview}"</p>}
                <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.createdAt, t)}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function Composer({ onPosted }: { onPosted?: () => void }) {
  const { t } = useTranslation(["social", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading } = useUpload({
    onError: (err) => toast({ title: t("social:toasts.uploadFailed"), description: err.message, variant: "destructive" }),
  });

  const createPost = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/dealer-social/posts", { body: body.trim(), mediaUrls });
      return res.json();
    },
    onSuccess: () => {
      setBody("");
      setMediaUrls([]);
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/feed"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/profile"] });
      toast({ title: t("social:toasts.posted") });
      onPosted?.();
    },
    onError: (err: any) => toast({ title: t("social:toasts.couldNotPost"), description: err.message, variant: "destructive" }),
  });

  async function handleFiles(files: FileList | null) {
    if (!files) return;
    const remaining = 4 - mediaUrls.length;
    const toUpload = Array.from(files).slice(0, remaining);
    for (const file of toUpload) {
      if (!file.type.startsWith("image/")) {
        toast({ title: t("social:toasts.onlyImages"), variant: "destructive" });
        continue;
      }
      const res = await uploadFile(file);
      if (res?.objectPath) setMediaUrls((prev) => [...prev, res.objectPath]);
    }
    if (fileRef.current) fileRef.current.value = "";
  }

  const canPost = (body.trim().length > 0 || mediaUrls.length > 0) && !createPost.isPending && !isUploading;

  return (
    <Card data-testid="composer">
      <CardContent className="pt-4 space-y-3">
        <Textarea
          data-testid="input-post-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t("social:composer.placeholder")}
          maxLength={2000}
          rows={3}
          className="resize-none"
        />
        {mediaUrls.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {mediaUrls.map((url, i) => (
              <div key={url} className="relative aspect-square rounded-md overflow-hidden border">
                <img src={objectUrl(url)} alt="" className="w-full h-full object-cover" data-testid={`img-preview-${i}`} />
                <button
                  type="button"
                  data-testid={`button-remove-image-${i}`}
                  onClick={() => setMediaUrls((prev) => prev.filter((u) => u !== url))}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="input-file"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="button-add-image"
            disabled={mediaUrls.length >= 4 || isUploading}
            onClick={() => fileRef.current?.click()}
            className="gap-1.5"
          >
            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            {t("social:composer.photo")}
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="button-submit-post"
            disabled={!canPost}
            onClick={() => createPost.mutate()}
            className="gap-1.5"
          >
            {createPost.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t("social:composer.post")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Feed({ search, isAdmin }: { search: string; isAdmin: boolean }) {
  const { t } = useTranslation(["social", "common"]);
  const { data, isLoading } = useQuery<{ items: SocialPostWithDetails[] }>({
    queryKey: ["/api/dealer-social/feed", search],
    queryFn: async () => {
      const url = search
        ? `/api/dealer-social/feed?search=${encodeURIComponent(search)}`
        : "/api/dealer-social/feed";
      return (await apiRequest("GET", url)).json();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
      </div>
    );
  }

  const posts = data?.items ?? [];
  if (posts.length === 0) {
    return (
      <Card data-testid="empty-feed">
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          {search ? t("social:feed.noMatch") : t("social:feed.empty")}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {posts.map((post) => <PostCard key={post.id} post={post} isAdmin={isAdmin} />)}
    </div>
  );
}

function PostCard({ post, isAdmin }: { post: SocialPostWithDetails; isAdmin: boolean }) {
  const { t } = useTranslation(["social", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showComments, setShowComments] = useState(false);

  const { data: me } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const isOwner = me?.id === post.authorUserId;
  const canDelete = isOwner || isAdmin;
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const react = useMutation({
    mutationFn: async (type: "like" | "dislike") =>
      (await apiRequest("POST", `/api/dealer-social/posts/${post.id}/reaction`, { type })).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/feed"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/profile"] });
    },
    onError: (err: any) => toast({ title: t("social:toasts.actionFailed"), description: err.message, variant: "destructive" }),
  });

  const del = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/dealer-social/posts/${post.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/feed"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/profile"] });
      toast({ title: t("social:toasts.postDeleted") });
    },
    onError: (err: any) => toast({ title: t("social:toasts.couldNotDelete"), description: err.message, variant: "destructive" }),
  });

  return (
    <Card data-testid={`card-post-${post.id}`}>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-start gap-3">
          <UserAvatar author={post.author} className="w-9 h-9" fallbackClassName="bg-violet-500/15 text-violet-500 text-xs font-semibold" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm" data-testid={`text-author-${post.id}`}>{post.author.displayName}</span>
              <span className="text-xs text-muted-foreground">@{post.author.username}</span>
              <span className="text-xs text-muted-foreground">· {timeAgo(post.createdAt, t)}</span>
            </div>
          </div>
          {canDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="w-7 h-7 text-muted-foreground hover:text-destructive"
              data-testid={`button-delete-post-${post.id}`}
              disabled={del.isPending}
              onClick={() => del.mutate()}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>

        {post.body && (
          <div className="text-sm" data-testid={`text-body-${post.id}`}>
            <RichText text={post.body} />
          </div>
        )}

        {post.mediaUrls.length > 0 && (
          <div className={`grid gap-2 ${post.mediaUrls.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
            {post.mediaUrls.map((url, i) => (
              <button
                key={url}
                type="button"
                onClick={() => setLightboxUrl(objectUrl(url))}
                className="block rounded-md overflow-hidden border cursor-pointer"
                data-testid={`button-open-image-${post.id}-${i}`}
              >
                <img src={objectUrl(url)} alt="" className="w-full max-h-80 object-cover" data-testid={`img-post-${post.id}-${i}`} />
              </button>
            ))}
          </div>
        )}

        <Dialog open={lightboxUrl !== null} onOpenChange={(o) => { if (!o) setLightboxUrl(null); }}>
          <DialogContent
            className="max-w-4xl w-fit p-0 bg-transparent border-0 shadow-none"
            data-testid={`dialog-lightbox-${post.id}`}
          >
            <DialogTitle className="sr-only">{t("social:post.image")}</DialogTitle>
            {lightboxUrl && (
              <img
                src={lightboxUrl}
                alt=""
                className="w-full max-h-[85vh] object-contain rounded-md"
                data-testid={`img-lightbox-${post.id}`}
              />
            )}
          </DialogContent>
        </Dialog>

        {post.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {post.hashtags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs" data-testid={`badge-hashtag-${tag}`}>#{tag}</Badge>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1 pt-1">
          <Button
            variant="ghost"
            size="sm"
            className={`gap-1.5 ${post.myReaction === "like" ? "text-blue-500" : "text-muted-foreground"}`}
            data-testid={`button-like-${post.id}`}
            disabled={react.isPending}
            onClick={() => react.mutate("like")}
          >
            <ThumbsUp className={`w-4 h-4 ${post.myReaction === "like" ? "fill-current" : ""}`} />
            <span data-testid={`text-like-count-${post.id}`}>{post.likeCount}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`gap-1.5 ${post.myReaction === "dislike" ? "text-blue-500" : "text-muted-foreground"}`}
            data-testid={`button-dislike-${post.id}`}
            disabled={react.isPending}
            onClick={() => react.mutate("dislike")}
          >
            <ThumbsDown className={`w-4 h-4 ${post.myReaction === "dislike" ? "fill-current" : ""}`} />
            <span data-testid={`text-dislike-count-${post.id}`}>{post.dislikeCount}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-muted-foreground"
            data-testid={`button-comments-${post.id}`}
            onClick={() => setShowComments((s) => !s)}
          >
            <MessageCircle className="w-4 h-4" />
            <span data-testid={`text-comment-count-${post.id}`}>{post.commentCount}</span>
          </Button>
        </div>

        {showComments && <Comments postId={post.id} isAdmin={isAdmin} meId={me?.id} />}
      </CardContent>
    </Card>
  );
}

function Comments({ postId, isAdmin, meId }: { postId: string; isAdmin: boolean; meId?: string }) {
  const { t } = useTranslation(["social", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [text, setText] = useState("");

  const { data, isLoading } = useQuery<{ comments: SocialCommentWithDetails[] }>({
    queryKey: ["/api/dealer-social/posts", postId, "comments"],
    queryFn: async () => (await apiRequest("GET", `/api/dealer-social/posts/${postId}/comments`)).json(),
  });

  const add = useMutation({
    mutationFn: async () =>
      (await apiRequest("POST", `/api/dealer-social/posts/${postId}/comments`, { body: text.trim() })).json(),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/posts", postId, "comments"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/feed"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/profile"] });
    },
    onError: (err: any) => toast({ title: t("social:toasts.couldNotComment"), description: err.message, variant: "destructive" }),
  });

  const del = useMutation({
    mutationFn: async (commentId: string) => apiRequest("DELETE", `/api/dealer-social/comments/${commentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/posts", postId, "comments"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/feed"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/profile"] });
    },
    onError: (err: any) => toast({ title: t("social:toasts.couldNotDelete"), description: err.message, variant: "destructive" }),
  });

  const react = useMutation({
    mutationFn: async ({ commentId, type }: { commentId: string; type: "like" | "dislike" }) =>
      (await apiRequest("POST", `/api/dealer-social/comments/${commentId}/reaction`, { type })).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/posts", postId, "comments"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/profile"] });
    },
    onError: (err: any) => toast({ title: t("social:toasts.actionFailed"), description: err.message, variant: "destructive" }),
  });

  const comments = data?.comments ?? [];

  return (
    <div className="space-y-3 pt-2">
      <Separator />
      <div className="flex items-center gap-2">
        <Input
          data-testid={`input-comment-${postId}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) { e.preventDefault(); add.mutate(); } }}
          placeholder={t("social:comments.placeholder")}
          maxLength={1000}
        />
        <Button
          size="sm"
          data-testid={`button-submit-comment-${postId}`}
          disabled={!text.trim() || add.isPending}
          onClick={() => add.mutate()}
        >
          {add.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : comments.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("social:comments.empty")}</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => {
            const canDelete = c.authorUserId === meId || isAdmin;
            return (
              <div key={c.id} className="flex items-start gap-2" data-testid={`comment-${c.id}`}>
                <UserAvatar author={c.author} className="w-7 h-7" fallbackClassName="bg-violet-500/15 text-violet-500 text-[10px] font-semibold" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold">{c.author.displayName}</span>
                    <span className="text-[11px] text-muted-foreground">@{c.author.username}</span>
                    <span className="text-[11px] text-muted-foreground">· {timeAgo(c.createdAt, t)}</span>
                  </div>
                  <div className="text-sm"><RichText text={c.body} /></div>
                  <div className="flex items-center gap-1 pt-0.5 -ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-6 gap-1 px-2 text-xs ${c.myReaction === "like" ? "text-blue-500" : "text-muted-foreground"}`}
                      data-testid={`button-like-comment-${c.id}`}
                      disabled={react.isPending}
                      onClick={() => react.mutate({ commentId: c.id, type: "like" })}
                    >
                      <ThumbsUp className={`w-3.5 h-3.5 ${c.myReaction === "like" ? "fill-current" : ""}`} />
                      <span data-testid={`text-like-count-comment-${c.id}`}>{c.likeCount}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-6 gap-1 px-2 text-xs ${c.myReaction === "dislike" ? "text-blue-500" : "text-muted-foreground"}`}
                      data-testid={`button-dislike-comment-${c.id}`}
                      disabled={react.isPending}
                      onClick={() => react.mutate({ commentId: c.id, type: "dislike" })}
                    >
                      <ThumbsDown className={`w-3.5 h-3.5 ${c.myReaction === "dislike" ? "fill-current" : ""}`} />
                      <span data-testid={`text-dislike-count-comment-${c.id}`}>{c.dislikeCount}</span>
                    </Button>
                  </div>
                </div>
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-muted-foreground hover:text-destructive"
                    data-testid={`button-delete-comment-${c.id}`}
                    disabled={del.isPending}
                    onClick={() => del.mutate(c.id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NotificationsBell() {
  const { t } = useTranslation(["social", "common"]);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: unread } = useQuery<{ count: number }>({
    queryKey: ["/api/dealer-social/notifications/unread-count"],
    queryFn: async () => (await apiRequest("GET", "/api/dealer-social/notifications/unread-count")).json(),
    refetchInterval: 30000,
    retry: false,
  });

  const { data, isLoading } = useQuery<{ items: SocialNotificationWithDetails[] }>({
    queryKey: ["/api/dealer-social/notifications"],
    queryFn: async () => (await apiRequest("GET", "/api/dealer-social/notifications")).json(),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/dealer-social/notifications/read", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/notifications/unread-count"] });
      qc.invalidateQueries({ queryKey: ["/api/dealer-social/notifications"] });
    },
  });

  const count = unread?.count ?? 0;
  const items = data?.items ?? [];

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && count > 0) markRead.mutate(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="button-notifications">
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span
              data-testid="badge-notifications-unread"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-violet-500 text-white text-[9px] font-bold flex items-center justify-center px-1"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 border-b">
          <p className="text-sm font-semibold">{t("social:notifications.title")}</p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-3 space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : items.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t("social:notifications.empty")}</p>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                data-testid={`notification-${n.id}`}
                className={`flex items-start gap-2 px-3 py-2.5 border-b last:border-b-0 ${n.read ? "" : "bg-violet-500/5"}`}
              >
                <UserAvatar author={n.actor} className="w-7 h-7" fallbackClassName="bg-violet-500/15 text-violet-500 text-[10px] font-semibold" />
                <div className="flex-1 min-w-0 text-sm">
                  <span className="font-semibold">{n.actor.displayName}</span>{" "}
                  <span className="text-muted-foreground">{notificationLabel(n, t)}</span>
                  {n.postPreview && <p className="text-xs text-muted-foreground truncate mt-0.5">"{n.postPreview}"</p>}
                  <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.createdAt, t)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
